/**
 * @file ProductDetailsSection.tsx
 * @description «Подробные характеристики» (phase08.5 shadcn): описание, состав,
 * хранение, характеристики «название — значение» (phase19.16 Р2а, SB33),
 * производитель.
 */

import { BookText } from "lucide-react";
import { useEffect, useState } from "react";

import { DirectoryAutocomplete } from "../../../../components/catalog/DirectoryAutocomplete";
import { ProducerEditDialog } from "../../../../components/catalog/ProducerEditDialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { RichTextEditor } from "../../../../components/ui/RichTextEditor";
import { Textarea } from "../../../../components/ui/textarea";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import {
  catalogProducers,
  type CatalogProducer,
} from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import type { ProductFormState } from "../product-form";
import { ProductAttributesEditor } from "./ProductAttributesEditor";
import { SectionCard } from "./SectionCard";

interface Props {
  form: ProductFormState;
  onChange: (patch: Partial<ProductFormState>) => void;
  disabled?: boolean;
}

export function ProductDetailsSection({ form, onChange, disabled }: Props) {
  const notify = useNotify();
  const [producers, setProducers] = useState<CatalogProducer[]>([]);
  const [creating, setCreating] = useState(false);

  async function refreshProducers() {
    try {
      setProducers(await catalogProducers.list());
    } catch (err) {
      notify.error(
        err instanceof ApiError ? err.message : "Не удалось загрузить производителей",
      );
    }
  }

  useEffect(() => {
    void refreshProducers();
  }, []);

  return (
    <SectionCard icon={<BookText />} title="Подробные характеристики" collapsible>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pd-desc">Полное описание</Label>
          {/* v0.18.1 — wysiwyg: заголовки/жирный/подчёркивание/списки/ссылки;
              HTML хранится в description и рендерится на странице товара. */}
          <RichTextEditor
            value={form.description}
            onChange={(html) => onChange({ description: html })}
            placeholder="История, способ применения, эффект…"
            disabled={disabled}
          />
          <p className="type-caption text-on-surface-variant">
            Форматирование (заголовки, списки, выделение) отобразится на странице товара.
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pd-comp">Состав / ингредиенты</Label>
          <Textarea
            id="pd-comp"
            value={form.composition}
            onChange={(e) => onChange({ composition: e.target.value })}
            disabled={disabled}
            rows={2}
          />
          <p className="type-caption text-on-surface-variant">
            В порядке убывания массовой доли
          </p>
        </div>
        {/* Срок годности (дней) переехал в «Вид товара и публикация» рядом с
            видом (phase19.22, W20): показывается, только если вид требует срок. */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pd-storage">Условия хранения</Label>
          <Input
            id="pd-storage"
            value={form.storageConditions}
            onChange={(e) => onChange({ storageConditions: e.target.value })}
            disabled={disabled}
            placeholder="В сухом прохладном месте"
          />
        </div>

        {/* phase19.16 Р2а (SB33) — характеристики «название — значение». */}
        <ProductAttributesEditor
          value={form.attributes}
          onChange={(attributes) => onChange({ attributes })}
          disabled={disabled}
        />

        <div className="flex flex-col gap-1.5">
          <Label>Производитель</Label>
          <DirectoryAutocomplete
            items={producers}
            value={form.producerId}
            onChange={(id) => onChange({ producerId: id })}
            onCreateClick={() => setCreating(true)}
            disabled={disabled}
            renderSecondary={(p) =>
              [p.legalAddress, p.inn ? `ИНН ${p.inn}` : null]
                .filter(Boolean)
                .join(" · ") || undefined
            }
          />
          <p className="type-caption text-on-surface-variant">
            Обязателен для публикации (закон «О защите прав потребителей», ст. 9)
          </p>
        </div>
      </div>

      {creating ? (
        <ProducerEditDialog
          open
          producer={null}
          onClose={() => setCreating(false)}
          onSaved={async (saved) => {
            setCreating(false);
            await refreshProducers();
            onChange({ producerId: saved.id });
          }}
        />
      ) : null}
    </SectionCard>
  );
}
